import { useCallback, useRef, useSyncExternalStore } from 'react';


type Listener = () => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StaticBootstrapStore<T = any> = {
	getSnapshot: () => T;
	setState: (next: T) => void;
	subscribe: (listener: Listener) => () => void;
};

/**
 * Creates a lightweight external store for StaticBootstrapContext values.
 */
export const createStaticBootstrapStore = <T>(
	initial: T,
): StaticBootstrapStore<T> => {
	let state = initial;
	const listeners = new Set<Listener>();

	return {
		getSnapshot: () => state,
		setState: (next: T) => {
			if (next !== state) {
				state = next;
				listeners.forEach((listener) => listener());
			}
		},
		subscribe: (listener: Listener) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
};

/**
 * A selector hook that subscribes to a specific slice of the StaticBootstrapStore.
 * Only triggers a re-render when the selected value changes (Object.is comparison).
 *
 * The getSnapshot function passed to useSyncExternalStore MUST return a cached/stable
 * reference when the selected value hasn't changed. Otherwise React detects a new value
 * on every call, triggers a re-render, which calls getSnapshot again — infinite loop.
 */
export function useStaticBootstrapSelector<S, T>(
	store: StaticBootstrapStore<S>,
	selector: (state: S) => T,
): T {
	const selectorRef = useRef(selector);
	selectorRef.current = selector;

	const cachedRef = useRef<{ value: T; state: S } | null>(null);

	const getSnapshot = useCallback((): T => {
		const currentState = store.getSnapshot();

		// If the underlying state hasn't changed, return the cached selected value.
		// This ensures referential stability for object/array selectors.
		if (cachedRef.current !== null && cachedRef.current.state === currentState) {
			return cachedRef.current.value;
		}

		const newValue = selectorRef.current(currentState);
		cachedRef.current = { value: newValue, state: currentState };
		return newValue;
	}, [store]);

	// Third argument (getServerSnapshot) is required for SSR compatibility.
	// We reuse getSnapshot because store.getSnapshot() has no browser dependencies —
	// it simply returns the current state, which during SSR is the default initial state.
	return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}
